/**
 * Simulation Utilities
 * Shared constants, types and helper functions for simulation components
 */

// Clinical examination types
export const EXAM_TYPES = [
  { id: 'auscultacion_cardiaca', label: 'Auscultación Cardíaca', icon: '❤️' },
  { id: 'auscultacion_pulmonar', label: 'Auscultación Pulmonar', icon: '🫁' },
  { id: 'palpacion_abdominal', label: 'Palpación Abdominal', icon: '🩺' },
  { id: 'examen_neurologico', label: 'Examen Neurológico', icon: '🧠' },
  { id: 'inspeccion_general', label: 'Inspección General', icon: '👁️' },
  { id: 'examen_extremidades', label: 'Extremidades', icon: '🦵' },
  { id: 'examen_piel', label: 'Examen de Piel', icon: '🩹' },
];

// Administration routes for medications
export const ADMINISTRATION_ROUTES = [
  { id: 'iv', label: 'Intravenoso (IV)' },
  { id: 'im', label: 'Intramuscular (IM)' },
  { id: 'sc', label: 'Subcutáneo (SC)' },
  { id: 'oral', label: 'Oral (VO)' },
  { id: 'sl', label: 'Sublingual (SL)' },
  { id: 'inhalado', label: 'Inhalado' },
  { id: 'topico', label: 'Tópico' },
  { id: 'rectal', label: 'Rectal' },
  { id: 'io', label: 'Intraóseo (IO)' },
];

// Simulation phases
export const SIMULATION_PHASES = {
  NOT_STARTED: 'not_started',
  ANAMNESIS: 'anamnesis',
  PHYSICAL_EXAM: 'examen_fisico',
  TESTS: 'examenes',
  TREATMENT: 'tratamiento',
  DEBRIEFING: 'debriefing',
  COMPLETED: 'completado',
};

// Protocol execution modes
export const PROTOCOL_MODES = {
  STRICT: 'estricto',
  OBSERVATIONAL: 'observacional',
  FREE: 'libre',
};

// Severity levels for blocked attempts
export const SEVERITY_LEVELS = {
  CRITICAL: 'critica',
  HIGH: 'alta',
  MEDIUM: 'media',
  LOW: 'baja',
};

/**
 * Format time in mm:ss format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get severity color class
 * @param {string} severity - Severity level
 * @returns {string} Tailwind CSS class
 */
export const getSeverityColor = (severity) => {
  switch (severity) {
    case SEVERITY_LEVELS.CRITICAL:
      return 'text-red-500 bg-red-900/30 border-red-700';
    case SEVERITY_LEVELS.HIGH:
      return 'text-orange-500 bg-orange-900/30 border-orange-700';
    case SEVERITY_LEVELS.MEDIUM:
      return 'text-yellow-500 bg-yellow-900/30 border-yellow-700';
    case SEVERITY_LEVELS.LOW:
      return 'text-blue-500 bg-blue-900/30 border-blue-700';
    default:
      return 'text-slate-500 bg-slate-900/30 border-slate-700';
  }
};

/**
 * Get patient state badge info
 * @param {object} patientState - Patient state object
 * @returns {object} Badge variant and label
 */
export const getPatientStateBadge = (patientState) => {
  const estado = patientState?.estado_general || 'estable';
  
  switch (estado) {
    case 'critico':
      return { variant: 'destructive', label: 'Crítico' };
    case 'inestable':
      return { variant: 'warning', label: 'Inestable' };
    case 'deteriorando':
      return { variant: 'warning', label: 'Deteriorándose' };
    case 'mejorando':
      return { variant: 'success', label: 'Mejorando' };
    case 'estable':
    default:
      return { variant: 'outline', label: 'Estable' };
  }
};

/**
 * Check if an action/medication is allowed in current protocol step
 * @param {object} protocolStatus - Current protocol status
 * @param {string} elementType - 'accion' or 'medicamento'
 * @param {string} elementId - ID of the element
 * @returns {object} { allowed: boolean, reason: string }
 */
export const checkProtocolAllowed = (protocolStatus, elementType, elementId) => {
  if (!protocolStatus?.has_strict_sequence) {
    return { allowed: true, reason: '' };
  }
  
  const currentStep = protocolStatus.paso_actual;
  if (!currentStep) {
    return { allowed: true, reason: 'No hay paso actual definido' };
  }
  
  // Check if element matches current expected step
  if (currentStep.tipo === elementType && currentStep.elemento_id === elementId) {
    return { allowed: true, reason: '' };
  }
  
  return {
    allowed: false,
    reason: `Paso actual esperado: ${currentStep.nombre}`,
  };
};

/**
 * Calculate progress percentage for protocol
 * @param {object} protocolStatus - Protocol status object
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProtocolProgress = (protocolStatus) => {
  if (!protocolStatus || !protocolStatus.total_pasos) return 0;
  
  const completed = protocolStatus.pasos_completados?.length || 0;
  return Math.round((completed / protocolStatus.total_pasos) * 100);
};
